/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import {
  Button,
  EmptyState,
  EmptyStateProps,
  ICON_INFO_WITH_LIST,
  SPINNER_TYPE_REGULAR,
  Spinner,
  Tab,
  Tabs,
} from '@squiz/sds';
import { getCapabilityPath } from 'apps/console-core/src/Utilities/GetCapabilityPath/GetCapabilityPath';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { CDP_STRINGS } from '../../../../Constants/Strings.constants';
import { EntitySegment, useSegments } from '../../../../Services/segmentation';
import { CUSTOMERS_DATA_IDS } from '../../SegmentInsights.constants';
import { CustomerList } from '../CustomerList/CustomerList';
import { SegmentPicker } from '../SegmentPicker/SegmentPicker';
import { SuccessMetricOutcome } from './elements/SuccessMetricOutcome/SuccessMetricOutcome';

/**
 * View customer information from a chosen segment.
 * @export
 * @returns {JSX.Element} The `<SegmentInsightsView />` component.
 */
export const SegmentInsightsView = (): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const segments = useSegments();
  const [segment, setSegment] = useState<EntitySegment | null>(null);

  const tabsKey = `tabs--${segment?.code || `empty`}`;

  // Labels
  const labels = useMemo(() => {
    return {
      customers: t(CDP_STRINGS.customers),
      goToSegmentation: t(CDP_STRINGS.goToSegmentation),
      noSegments: t(CDP_STRINGS.noSegmentsCaption),
    };
  }, [t]);

  const customerViewTabs = useMemo((): Array<Tab> => {
    return [
      {
        content: <CustomerList activeSegment={segment} />,
        key: `customers`,
        label: labels.customers,
      },
      {
        content: <SuccessMetricOutcome activeSegment={segment} />,
        key: `success-metric-outcome`,
        label: `Success metric outcome`,
      },
    ];
  }, [labels, segment]);

  // Loading state
  if (!segments) {
    return (
      <div className="loading">
        <Spinner type={SPINNER_TYPE_REGULAR} />
      </div>
    );
  }

  // Empty state
  if (segments && segments.length === 0) {
    const emptyButton = (
      <Button
        dataId={CUSTOMERS_DATA_IDS.customersEmptyStateButton}
        label={labels.goToSegmentation}
        onClick={(): void => navigate(getCapabilityPath(`/segmentation`))}
        text={labels.goToSegmentation}
      />
    );
    const emptyStateProps: EmptyStateProps = {
      caption: labels.noSegments,
      children: emptyButton,
      dataId: CUSTOMERS_DATA_IDS.customersEmptyState,
      svg: ICON_INFO_WITH_LIST,
    };

    return <EmptyState {...emptyStateProps} />;
  }

  // Page content
  return (
    <>
      <SegmentPicker activeSegment={segment} segments={segments} setActiveSegment={setSegment} />
      {segment && (
        <div className="customers__container">
          <Tabs classes="customers__tabs" key={tabsKey} listLabel={labels.customers} tabList={customerViewTabs} />
        </div>
      )}
    </>
  );
};
